import { useEffect, useRef, useState } from "react";
import { Star, ArrowRight } from "lucide-react";

const timelineData = [
  {
    year: "2017",
    title: "Lahirnya Oerang Borneo",
    description:
      "Berawal dari semangat independen dan kecintaan terhadap dunia visual storytelling.",
  },
  {
    year: "2020",
    title: "Berkembang & Bertumbuh",
    description:
      "Menjadi wadah kreatif bagi sineas, kreator, dan talenta berbakat di Kalimantan.",
  },
  {
    year: "2025",
    title: "Legalitas Resmi",
    description:
      "Tonggak penting dalam perjalanan kami menuju kolaborasi yang lebih luas dan berdampak.",
  },
  {
    year: "Future",
    title: "Visi Masa Depan",
    description:
      "Terus berkarya dan membawa cerita Borneo ke panggung nasional dan internasional.",
  },
];

export default function AboutPage() {
  const [isVisible, setIsVisible] = useState(false);
  const [visibleItems, setVisibleItems] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            timelineData.forEach((_, index) => {
              setTimeout(() => {
                setVisibleItems((prev) => [...prev, index]);
              }, index * 200);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen pt-16 sm:pt-20 bg-[#0A1628]">
      {/* Background Glow */}
      <div className="fixed bottom-0 right-0 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] bg-[#E8761A]/5 rounded-full blur-3xl pointer-events-none" />

      <div ref={sectionRef} className="relative z-10 py-8 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div
            className={`text-center mb-10 sm:mb-16 lg:mb-20 transition-all duration-800 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <span
              data-testid="about-badge"
              className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-[#E8761A]/10 border border-[#E8761A]/30 rounded-full text-[#E8761A] text-xs sm:text-sm font-medium tracking-wider uppercase mb-4 sm:mb-6"
            >
              About Us
            </span>
            <h1
              data-testid="about-title"
              className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white mb-4 sm:mb-6"
            >
              Cerita <span className="text-[#E8761A]">Oerang Borneo</span>
            </h1>
            <div className="section-divider mx-auto" />
          </div>

          {/* Main Story */}
          <div
            className={`max-w-4xl mx-auto mb-12 sm:mb-16 lg:mb-20 transition-all duration-800 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <div className="bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl p-5 sm:p-8 lg:p-12">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#E8761A]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Star className="w-5 h-5 sm:w-6 sm:h-6 text-[#E8761A]" />
                </div>
                <div>
                  <h2
                    data-testid="about-story-title"
                    className="text-lg sm:text-xl lg:text-2xl font-semibold text-white mb-2"
                  >
                    Visual Storyteller from Borneo
                  </h2>
                  <div className="w-12 sm:w-16 h-1 bg-[#E8761A] rounded-full" />
                </div>
              </div>
              <p
                data-testid="about-story-content"
                className="text-white/70 leading-relaxed text-sm sm:text-base lg:text-lg"
              >
                Oerang Borneo lahir pada tahun 2017 dari semangat independen dan kecintaan
                terhadap dunia visual storytelling. Selama bertahun-tahun, kami telah
                berkembang menjadi wadah kreatif bagi sineas, kreator, dan talenta berbakat.
                Kami memandang film bukan sekadar hiburan, melainkan sarana menyampaikan
                gagasan, identitas, dan pesan yang bermakna. Pada 2025, Oerang Borneo resmi
                mendapatkan legalitas perusahaan — sebuah tonggak penting dalam perjalanan
                kami menuju kolaborasi yang lebih luas dan berdampak.
              </p>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative">
            <h3 className={`text-xl sm:text-2xl font-semibold text-white mb-8 sm:mb-10 text-center transition-all duration-800 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}>
              Perjalanan <span className="text-[#E8761A]">Kami</span>
            </h3>
            
            {/* Timeline Line - Desktop */}
            <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#E8761A]/30 to-transparent" />

            {/* Timeline Items */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-4">
              {timelineData.map((item, index) => (
                <div
                  key={item.year}
                  data-testid={`timeline-item-${item.year.toLowerCase()}`}
                  className={`relative transition-all duration-700 ${
                    visibleItems.includes(index)
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-10"
                  }`}
                >
                  {/* Mobile Timeline Line */}
                  <div className="lg:hidden absolute left-4 top-0 bottom-0 w-0.5 bg-[#E8761A]/30" />

                  {/* Node */}
                  <div className="relative flex items-start lg:flex-col lg:items-center gap-4 sm:gap-6 lg:gap-4">
                    {/* Dot */}
                    <div className="relative z-10 flex-shrink-0">
                      <div className="w-8 h-8 lg:w-12 lg:h-12 bg-[#0A1628] border-2 border-[#E8761A] rounded-full flex items-center justify-center shadow-lg shadow-[#E8761A]/20">
                        <div className="w-3 h-3 lg:w-4 lg:h-4 bg-[#E8761A] rounded-full animate-pulse-glow" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="lg:text-center flex-1">
                      <span className="text-[#E8761A] font-bold text-xl sm:text-2xl lg:text-3xl">
                        {item.year}
                      </span>
                      <h4 className="text-white font-semibold text-base sm:text-lg mt-1 sm:mt-2 mb-1 sm:mb-2">
                        {item.title}
                      </h4>
                      <p className="text-white/60 text-xs sm:text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>

                  {/* Arrow for Desktop */}
                  {index < timelineData.length - 1 && (
                    <div className="hidden lg:flex absolute top-[88px] -right-2 text-[#E8761A]/50">
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div
            className={`text-center mt-12 sm:mt-16 lg:mt-20 transition-all duration-800 delay-500 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <p className="text-white/60 mb-4 sm:mb-6 text-sm sm:text-base px-2">
              Mari berkolaborasi dan ciptakan karya visual yang bermakna bersama kami
            </p>
            <a
              href="https://wa.me/6289516500862"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-cta"
              className="btn-primary inline-flex items-center gap-2 group text-sm sm:text-base"
            >
              Mulai Kolaborasi
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
